// src/lib/auth/logout.init.js
// Backend numeric enum bekliyor: SingleSession=0, CurrentSession=1, SpecificSessionByContext=2, AllDevices=3
//
// Attributes:
// - data-logout-type="1"                (required) numeric recommended
// - data-logout-confirm="..."           confirm message
// - data-logout-redirect="/Auth/Login"  override global redirect
// - data-logout-silent="true"           don't alert on error
// - data-logout-no-redirect="true"      do not redirect after success
//
// Built-ins:
// - spam / double click guard
// - optional global cooldown (default 2s) after a successful attempt
// - uses RequestVerificationToken header (ASP.NET antiforgery)

// <!-- normal çıkış (CurrentSession=1) -->
// <button data-logout-type="1">Çıkış</button>

// <!-- tüm cihazlar (AllDevices=3) -->
// <button data-logout-type="3"
//         data-logout-confirm="Tüm cihazlardan çıkış yapmak istediğine emin misin?"
//         data-logout-redirect="/Auth/Login">
//   Tüm Cihazlardan Çıkış
// </button>

// <!-- redirect istemiyorum -->
// <button data-logout-type="1" data-logout-no-redirect="true">Logout (stay)</button>
let _initialized = false;
let _globalInFlight = false;
let _cooldownUntil = 0;

function nowMs() {
  return Date.now();
}

function parseBoolAttr(el, attrName) {
  const v = el.getAttribute(attrName);
  if (v == null) return false;
  if (v === "") return true;
  const s = String(v).toLowerCase().trim();
  return s === "1" || s === "true" || s === "yes" || s === "on";
}

function parseLogoutTypeNumber(raw) {
  const n = Number(raw);
  if (Number.isFinite(n) && n >= 0 && n <= 3) return n;
  return 1; // CurrentSession
}

function setBusy(el, isBusy) {
  el.dataset.busy = isBusy ? "1" : "0";
  if ("disabled" in el) el.disabled = isBusy;
  else el.setAttribute("aria-busy", isBusy ? "true" : "false");
  el.classList.toggle("is-loading", isBusy);
}

function buildAbortTimeout(timeoutMs) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);
  return { controller, clear: () => clearTimeout(t) };
}

function safePreview(s, max = 28) {
  if (!s) return "";
  const str = String(s);
  return str.length <= max ? str : str.slice(0, max) + "...";
}

async function fetchCsrfToken({ csrfEndpoint, timeoutMs, debug }) {
  const { controller, clear } = buildAbortTimeout(timeoutMs);
  try {
    const res = await fetch(csrfEndpoint, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal: controller.signal,
    });

    if (!res.ok) throw new Error(`CSRF HTTP ${res.status}`);

    const json = await res.json().catch(() => null);
    const token = json?.token;

    if (!token) throw new Error("CSRF token missing in response");

    return token;
  } finally {
    clear();
  }
}

async function performLogout({ endpoint, logoutTypeNumber, csrfEndpoint, timeoutMs, debug }) {
  // Always fetch a fresh antiforgery token to avoid stale/mismatch issues
  const csrfToken = await fetchCsrfToken({ csrfEndpoint, timeoutMs, debug });

  const { controller, clear } = buildAbortTimeout(timeoutMs);
  try {
    const res = await fetch(endpoint, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Accept: "application/json",
        RequestVerificationToken: csrfToken, // matches AddAntiforgery header name
      },
      body: JSON.stringify({ logoutType: logoutTypeNumber }),
      cache: "no-store",
      signal: controller.signal,
    });

    const text = await res.text().catch(() => "");
    const json = text
      ? (() => {
          try {
            return JSON.parse(text);
          } catch {
            return null;
          }
        })()
      : null;

    if (!res.ok) {
      if (debug) {
        console.warn("[LOGOUT] HTTP fail:", res.status, res.statusText);
        console.warn("[LOGOUT] response body:", text);
      }
      throw new Error(`Logout HTTP ${res.status}`);
    }

    if (!json?.success) {
      throw new Error(json?.message || "Logout başarısız.");
    }

    return json;
  } finally {
    clear();
  }
}

/**
 * initLogout(): call once in entry.
 */
export function initLogout({
  endpoint = "/Auth/Logout",
  csrfEndpoint = "/Auth/Csrf", // <-- your new endpoint
  redirectTo = "/Auth/Login",
  attribute = "data-logout-type",

  // behavior
  disableWhileRunning = true,
  globalLock = true,
  cooldownMs = 2000,

  // network
  timeoutMs = 15000,

  // confirm for AllDevices (3)
  defaultConfirmAllDevices = true,
  defaultConfirmAllDevicesMessage = "Tüm cihazlardan çıkış yapmak istediğine emin misin?",

  // debug logs
  debug = false,
} = {}) {
  if (_initialized) return;
  _initialized = true;

  document.addEventListener("click", async (e) => {
    const el = e.target.closest(`[${attribute}]`);
    if (!el) return;

    e.preventDefault();

    // global cooldown
    if (cooldownMs > 0 && nowMs() < _cooldownUntil) {
      return;
    }

    // per-button busy guard
    if (disableWhileRunning && el.dataset.busy === "1") {
      return;
    }

    // global in-flight guard
    if (globalLock && _globalInFlight) {
      return;
    }

    const rawType = el.getAttribute(attribute);
    const logoutTypeNumber = parseLogoutTypeNumber(rawType);

    // per-button overrides
    const perRedirect = el.getAttribute("data-logout-redirect") || redirectTo;
    const silent = parseBoolAttr(el, "data-logout-silent");
    const noRedirect = parseBoolAttr(el, "data-logout-no-redirect");

    // confirm logic
    const confirmMsg = el.getAttribute("data-logout-confirm");
    if (confirmMsg) {
      if (!window.confirm(confirmMsg)) return;
    } else if (defaultConfirmAllDevices && logoutTypeNumber === 3) {
      if (!window.confirm(defaultConfirmAllDevicesMessage)) return;
    }

    try {
      if (globalLock) _globalInFlight = true;
      if (disableWhileRunning) setBusy(el, true);

      const result = await performLogout({
        endpoint,
        logoutTypeNumber,
        csrfEndpoint,
        timeoutMs,
        debug,
      });

      // cooldown after any attempt that reached server
      if (cooldownMs > 0) _cooldownUntil = nowMs() + cooldownMs;

      if (!noRedirect && perRedirect) {
        window.location.replace(perRedirect);
      }

      return result;
    } catch (err) {
      console.error("[LOGOUT] error:", err);

      // also cooldown on error to prevent spam
      if (cooldownMs > 0) _cooldownUntil = nowMs() + cooldownMs;

      if (!silent) alert("Çıkış yapılırken hata oluştu.");
    } finally {
      if (disableWhileRunning) setBusy(el, false);
      if (globalLock) _globalInFlight = false;
    }
  });
}
